import { useCallback, useState } from 'react';

/**
 * Pagination state hook. Owns `page`, exposes `setPage`/`next`/`prev`,
 * and updates total/totalPages from a response meta object.
 *
 *   const pagination = usePagination();
 *   const { data: response } = useFetch(
 *     () => getCoursePayments(courseId, { page: pagination.page, limit: pagination.limit }),
 *     [pagination.page],
 *   );
 *   useEffect(() => { pagination.applyMeta(response); }, [response]);
 *
 * The `applyMeta` setter is stable so it is safe in effect deps.
 */
export function usePagination(initial = {}) {
  const [state, setState] = useState({
    page: initial.page ?? 1,
    limit: initial.limit ?? 20,
    total: 0,
    totalPages: 1,
  });

  const setPage = useCallback((page) => {
    setState((prev) => ({ ...prev, page: Math.max(1, page) }));
  }, []);

  const next = useCallback(() => {
    setState((prev) =>
      prev.page < prev.totalPages ? { ...prev, page: prev.page + 1 } : prev,
    );
  }, []);

  const prev = useCallback(() => {
    setState((p) => (p.page > 1 ? { ...p, page: p.page - 1 } : p));
  }, []);

  const reset = useCallback(() => {
    setState((p) => ({ ...p, page: 1 }));
  }, []);

  const applyMeta = useCallback((meta) => {
    if (!meta) return;
    setState((p) => ({
      ...p,
      total: meta.total ?? p.total,
      totalPages: meta.totalPages ?? p.totalPages,
    }));
  }, []);

  return { ...state, setPage, next, prev, reset, applyMeta };
}
